import * as React from 'react';

import _ from 'lodash';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';

import { Notification } from '@coursera/coursera-ui';
import { email as isEmailInvalid } from '@coursera/coursera-ui/lib/utils/validation';
import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import SettingsServerActionCreators from 'bundles/account-settings/actions/SettingsServerActionCreators';
import SaveButton from 'bundles/account-settings/components/common/SaveButton';
import StatusMessage from 'bundles/account-settings/components/common/StatusMessage';
import SettingsConstants from 'bundles/account-settings/constants/SettingsConstants';

import _t from 'i18n!nls/account-settings';

import 'css!./__styles__/Password';

const { Fields, UpdateStates, Sections } = SettingsConstants;

type Props = {
  updateStatus: any;
  invalidFields: Array<any>;
  secondaryEmail: UserEmail | undefined;
  hasGeneratedPassword: boolean;
};
type State = {
  email: string;
};

export default class PersonalAccount extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { secondaryEmail } = props;
    this.state = {
      email: secondaryEmail?.emailAddress || '',
    };
  }

  /*
  case 1
  no alternative email
  has generated password
    update alternative email
    reset pwd

  case 2
  have alternative email
  has generated password
    show banner
    reset pwd

  case 3 legacy case
  no alternative email
  have password but not generated
    update alternative email

  case 4 (currently not enabled because we don't have the endpoint to update alternative email)
  have alternative email and password

  */
  handleSaveButton = () => {
    const { secondaryEmail, hasGeneratedPassword } = this.props;
    const { email } = this.state;

    if (isEmailInvalid(email)) {
      SettingsServerActionCreators.updateInvalidEmail(Sections.PERSONAL_ACCOUNT);
      return;
    }

    if (!secondaryEmail && hasGeneratedPassword) {
      SettingsActionCreators.addAlternativeEmail(email).then((x) => {
        SettingsActionCreators.requestResetPassword();
      });
    } else if (hasGeneratedPassword) {
      SettingsActionCreators.requestResetPassword();
    } else if (!secondaryEmail) {
      SettingsActionCreators.addAlternativeEmail(email);
    }
  };

  onEmailFieldChange = (event: React.FocusEvent<HTMLInputElement>) => {
    const email = event.target.value;
    if (isEmailInvalid(email)) {
      SettingsServerActionCreators.updateInvalidEmail(Sections.PERSONAL_ACCOUNT);
    } else {
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
      SettingsActionCreators.resetSection();
    }
    this.setState({ email });
  };

  renderValidation(field: string) {
    const { invalidFields, updateStatus } = this.props;
    if (_.includes(invalidFields, field)) {
      return <StatusMessage status={UpdateStates.FAILED} field={field} />;
    }
    if (updateStatus.state) {
      return <StatusMessage status={updateStatus.state} />;
    }
    return null;
  }

  render() {
    const { updateStatus, hasGeneratedPassword, secondaryEmail } = this.props;
    const { email } = this.state;
    const updateSucceded = updateStatus.state === UpdateStates.SUCCEEDED;
    const shouldShowBanner = !!secondaryEmail && hasGeneratedPassword;
    const isCase4 = !!secondaryEmail && !hasGeneratedPassword;

    return (
      <div className="rc-Password">
        <h2 className="section-title">{_t('Personal Account')}</h2>
        <p>
          {_t(
            'Add your personal account here, so you’ll still have access to Coursera courses after you leave your current company.'
          )}
        </p>
        <div className="horizontal-box wrap">
          <div className="settings-input-wrapper flex-3">
            <label htmlFor="settings-alternative-email">{_t('Add Alternative Email')}</label>
            <input
              id="settings-alternative-email"
              type="text"
              placeholder={_t('Enter personal email')}
              defaultValue={email}
              disabled={updateSucceded || !!secondaryEmail}
              onBlur={this.onEmailFieldChange}
            />
            {this.renderValidation(Fields.ALTERNATIVE_EMAIL)}
          </div>
        </div>
        {shouldShowBanner && (
          <div style={{ marginRight: 10, marginBottom: 10 }}>
            <Notification
              type="danger"
              message={_t(
                'Your personal account is not set up yet, please re-send the email and follow the instructions.'
              )}
              action={_t('Re-send the email')}
              onAction={this.handleSaveButton}
            />
          </div>
        )}
        <div className="horizontal-box">
          <div>
            <SaveButton
              onClick={this.handleSaveButton}
              text={hasGeneratedPassword ? _t('Create Password') : _t('Save')}
              updating={updateStatus.state === UpdateStates.UPDATING}
              disabled={isCase4 || updateSucceded}
            />
          </div>
          <div className="m-t-1s">
            {updateSucceded && (
              <StatusMessage
                status={updateStatus.state}
                customMessage={
                  hasGeneratedPassword ? (
                    <FormattedHTMLMessage
                      message={_t(
                        'An email has been sent to <b>#{email}</b>. Please follow the instructions in the link to set up your personal account.',
                        { email: user.get().email_address }
                      )}
                    />
                  ) : (
                    _t('Alternative email added!')
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}
